// Resolve a stored Sohoz Job customer record into display-ready sections.
//
// Form values are stored as the government form's option VALUES/IDs (e.g. religion
// "1", board "14", duration "03"). This module maps those back to human labels via
// the same field configs the form uses, so the preview / print / PDF show readable
// text instead of raw IDs.

import { format } from 'date-fns';
import {
    BASIC_FIELDS, ADDRESS_BASE, QUOTA_SELECTS, PHYSICAL_FIELDS, FAMILY_FIELDS, FAMILY_MEMBER_FIELDS,
    EXPERIENCE_FIELDS, academicFieldsFor, ACADEMIC_FALLBACK, FREEDOM_FIGHTER_QUOTAS, FREEDOM_FIGHTER_FIELDS,
} from './customerFields';

const fmtDate = (v) => {
    if (!v) return '';
    const d = new Date(`${v}T00:00:00`);
    return Number.isNaN(d.getTime()) ? String(v) : format(d, 'dd MMM yyyy');
};

const fieldLabel = (field, t) => t(`sohozJob.f.${field.labelKey || field.name}`);

// Resolve a select/combo value to its option label.
const optLabel = (field, value, t) => {
    const opts = field.options || [];
    const found = opts.find((o) => (typeof o === 'string' ? o : o.value) === value);
    if (found === undefined) return String(value);
    if (typeof found === 'string') return field.rawOptions ? found : t(`sohozJob.opts.${found}`);
    return found.label != null ? found.label : t(`sohozJob.opts.${found.key}`);
};

const formatValue = (field, value, t) => {
    if (value == null || value === '') return '';
    if (field.type === 'date') return fmtDate(value);
    if (field.type === 'checkbox') return value ? t('sohozJob.opts.yes') : '';
    if (field.type === 'select' || field.type === 'combo') return optLabel(field, value, t);
    return String(value);
};

// Expand a field list to include any reveal child fields inline.
const expand = (fields) => {
    const out = [];
    for (const f of fields) {
        out.push(f);
        if (f.reveal?.field) out.push(f.reveal.field, ...(f.reveal.extra || []));
    }
    return out;
};

// Build [{ label, value }] rows for a field list against `data`, skipping empties.
const rowsFor = (fields, data, t, { prefix = '', exclude = [] } = {}) => {
    const rows = [];
    for (const f of expand(fields)) {
        if (exclude.includes(f.name)) continue;
        const val = formatValue(f, data[prefix + f.name], t);
        if (val !== '') rows.push({ label: fieldLabel(f, t), value: val });
    }
    return rows;
};

const entriesFor = (rows, data, listKey, fieldsFor, t) => {
    const arr = Array.isArray(data[listKey]) ? data[listKey] : [];
    return arr
        .map((row) => {
            // `_`-prefixed keys are bookkeeping (the academic catalog stamp), so a row
            // carrying only those is still empty.
            if (!row || !Object.entries(row).some(([k, v]) => !k.startsWith('_') && v !== '' && v != null)) return null;
            const items = [];
            if (row.level) items.push({ label: t('sohozJob.f.level'), value: t(`sohozJob.opts.${row.level}`) });
            const fields = fieldsFor(row);
            for (const f of fields) {
                const val = formatValue(f, row[f.name], t);
                if (val !== '') items.push({ label: fieldLabel(f, t), value: val });
            }
            return items.length ? items : null;
        })
        .filter(Boolean);
};

// Convert a full customer record into the structured model the document renders.
export function customerToSections(data, t) {
    const d = data || {};

    const header = {
        photo: d.photo || '',
        signature: d.signature || '',
        nameBn: d.applicant_name_bn || '',
        nameEn: d.applicant_name_en || '',
        mobile: d.mobile_number || '',
    };

    const sections = [];

    const basic = rowsFor(BASIC_FIELDS, d, t, {
        exclude: ['applicant_name_bn', 'applicant_name_en', 'mobile_number'],
    });
    if (basic.length) sections.push({ title: t('sohozJob.sections.basic'), rows: basic });

    const present = rowsFor(ADDRESS_BASE, d, t, { prefix: 'present_' });
    if (present.length) sections.push({ title: t('sohozJob.sections.presentAddress'), rows: present });

    const permanent = rowsFor(ADDRESS_BASE, d, t, { prefix: 'permanent_' });
    if (permanent.length) sections.push({ title: t('sohozJob.sections.permanentAddress'), rows: permanent });

    const quota = rowsFor(QUOTA_SELECTS, d, t);
    if (quota.length) sections.push({ title: t('sohozJob.sections.quota'), rows: quota });

    if (FREEDOM_FIGHTER_QUOTAS.includes(String(d.quota || '')) && d.freedom_fighter && typeof d.freedom_fighter === 'object') {
        const ff = rowsFor(FREEDOM_FIGHTER_FIELDS, d.freedom_fighter, t);
        if (ff.length) sections.push({ title: t('sohozJob.sections.freedomFighter'), rows: ff });
    }

    const physical = rowsFor(PHYSICAL_FIELDS, d, t);
    if (physical.length) sections.push({ title: t('sohozJob.sections.physical'), rows: physical });

    const family = rowsFor(FAMILY_FIELDS, d, t);
    // One line per other family member: "name — relation — phone".
    (Array.isArray(d.family_members) ? d.family_members : []).forEach((m, i) => {
        const line = FAMILY_MEMBER_FIELDS.map((f) => m?.[f.name]).filter(Boolean).join(' — ');
        if (line) family.push({ label: `${t('sohozJob.familyMemberEntry')} ${i + 1}`, value: line });
    });
    if (family.length) sections.push({ title: t('sohozJob.sections.family'), rows: family });

    const academic = entriesFor([], d, 'academic', (row) => (row.level ? academicFieldsFor(row.level) : ACADEMIC_FALLBACK), t);
    const experience = entriesFor([], d, 'experience', () => EXPERIENCE_FIELDS, t);

    return { header, sections, academic, experience };
}
